/* eslint-disable max-lines */
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";
import type { CouponStatus } from "@swiggy-private/dls-web-recipe-do-web/dist/components/PurchasedCouponCard";

export enum WidgetAlignment {
    Left = "left",
    Center = "center",
    End = "end",
}

export interface ICharge {
    type: string;
    value: number;
    ctx?: {
        displayIcon?: string;
        displayName?: string;
        description?: string;
    };
}

export interface IDineoutPricingInfo {
    gst?: { totalGST?: number }[];
    billToPay?: number;
    advanceAmount?: number;
    totalBillExcludingExtras?: number;
    billAmountToApplyCoupon?: number;
    totalDiscount: number;
    totalDiscountPercentage: number;
    totalMiscCharge: number;
    enteredBillAmount: number;
    totalRewardsRedeemed?: number;
    totalBill?: number;
}

export interface IDiscount {
    type: string;
    ctx?: {
        isAggregatedDiscount?: boolean;
        offerName?: string;
        percentage?: number;
        AGGREGATED_OFFER_TYPE?: string;
        offerType?: string;
        offerId?: string;
        offerLogo?: string;
        offerTitle?: string;
        displayName?: string;
        description?: string;
    };
    value: string;
    description?: string;
    superOffer: boolean;
    couponType: string;
    allianceIdentifier?: string;
    couponDiscount?: string;
}

interface ITicketTermsAndCondition {
    tnc: string;
}

interface ITicketThingsToRemember {
    listOfThingsToRemember: string[];
    noteText: string;
}

export interface ITicketData {
    basePrice: number;
    dealEndDate: string;
    dealSubCategory: string;
    description: string;
    discountPercentage: number;
    discountValue: number;
    effectivePrice: number;
    guestCount: number;
    redeemable: boolean;
    tags: string[];
    ticketTermsAndCondition: ITicketTermsAndCondition;
    ticketThingsToRemember: ITicketThingsToRemember;
    ticketTitle: string;
    totalBasePrice: number;
    totalEffectivePrice: number;
    validDays: string;
    validTime: string;
    variantTitle: string;
    isVeg?: VEG_TYPE;
    title?: string;
    maxAvailableQuantity?: number;
}

export interface ICtaItem {
    link: string;
    text: string;
    type: string;
}

export interface ISavingTextStyle {
    variant: string;
    color: string;
    defaultVariant: string;
    defaultColor: string;
    style: {
        lineHeight: number;
        fontWeight: number;
        letterSpacing: number;
        fontSize: number;
    };
    shouldAllowParser: boolean;
}

interface CancellationDisclaimer {
    title: {
        text: string;
        color: string;
        variant: string;
    };
    description: {
        text: string;
        color: string;
        variant: string;
    };
}
export interface IWidget {
    layoutId: number;
    id: number;
    active: number;
    widgetInfo?: IWidgetInfo;
    priority: number;
}

export interface IWidgetInfo {
    widgetType: string;
    title?: string;
    affordance: boolean;
    text?: string;
    autoScrollTime: number;
    savingsImage?: string;
    helpIconImage?: string;
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
    girforder?: boolean;
    savingsText?: string;
    parentStyle?: IParentStyle;
    savingTextStyle?: ISavingTextStyle;
    mastheadBackgroundImage: string;
    tickerTextColor?: keyof IColors;
    mastheadRewardsBackgroundImage?: string;
    popupSavingsBackgroundImage: string;
    dineoutMastHeadTextWidget: {
        affordance: boolean;
        autoScrollTime: number;
        color: string;
        text: string;
        widgetType: string;
    };
    rewardsText?: string;
    savingsTextColor?: string;
    savingsTextVariant?: string;
    icon?: string;
    leftIcon?: string;
    alignment?: WidgetAlignment;
    cancellationPolicyWidget?: CancellationDisclaimer;
}

interface IParentStyle {
    style: {
        flex: number;
        marginHorizontal: number;
        height: number;
        borderRadius: number;
        marginTop: number;
    };
    angle: number;
    gradientColor: string[];
    gradientType: string;
}

export interface IAggregatedItemsInfo {
    dateDisplayTitle: string;
    timeDisplayTitle: string;
    ticketCountTitle: string;
    isAnyTicketSoldOut: boolean;
    ticketTitle: string;
    eventId?: string;
}

export interface IDineoutCartResponseBillData {
    dealCategory: string;
    skipPayment: boolean;
    discounts?: IDiscount[];
    dineoutPricingInfo: IDineoutPricingInfo;
    charges?: ICharge[];
    coverCount?: number;
}

export interface IStoresInfo {
    name: string;
    locality: string;
    cityName: string;
    id?: number;
    vendorIdForApplyCoupon?: string;
    displayAreaName?: string;
    address: string;
    landmark: string;
    maxAllowedCartVolumeInCc: number;
    maxAllowedCartWeightInGrams: number;
    maxAllowedItemsInCart: number;
    minimumOrderValueInRs: number;
    restaurantLatLong: [number];
}

export enum VEG_TYPE {
    NON_VEG,
    VEG,
    EGG,
}

export interface ICouponInfo {
    title: string;
    subtitle: string;
    code: string;
    statusTimestamp: number;
    status: CouponStatus
}

export interface IResponseCartItem {
    itemId: string;
    bookingDay: string;
    bookingDate: string;
    bookingSlot: string;
    bookingSlotTime: string;
    bookingStatus?: string;
    savingsLine?: string;
    name?: string;
    guestCount: string;
    quantity: number;
    price?: number;
    itemName: string;
    min: number;
    max: number;
    ticketAttributes: ITicketData;
    itemVariantId: string;
    serviceLine: string;
    outOfStock?: boolean;
    rewardsWidget?: IRewardsWidget;
    ticketPresentationInfo: {
        ticketLogo: string;
        colorCodes: string[];
    };
    validityDuration?: {
        type: string;
        value?: string;
    };
    validitySubtext?: string;
    validOnAllOutlets?: boolean;
    eligibleOutlets?: string[];
    ineligibleOutlets?: string[];
    tncs?: string[];
    minimumQtyBreach?: boolean;
    logoId?: string;
    images?: string[];
    couponInfo?: ICouponInfo;
}

export interface IRewardsWidget {
    backgroundImage: string;
    logo: string;
    title: string;
    boosterBackgroundImage?: string;
    boosterAnimation?: string;
    header?: IRewardWidgetHeader;
    isBooster?: boolean;
}

export interface IRewardWidgetHeader {
    title?: string;
    subtitle?: string;
    logo?: string;
    callout?: string;
}

export interface IFooter {
    coverChargeRemainingSubtext: string;
    coverChargeRemainingText: string;
    coverChargeRemainingTextColor: string;
    coverChargesExhausted: boolean;
    ctaList: ICtaItem[];
}

export interface ICallout {
    body: string;
    color: string;
    enabled: boolean;
    forward: boolean;
    title: string;
    type: string;
    icon?: string;
}

export interface ISubscriptionWidget {
    backgroundImage?: string;
    girforder?: boolean;
    image?: string;
    line1?: Record<string, { color: string; value: string }[]>;
    line2?: Record<string, { color: string; value: string }[]>;
    line3?: Record<string, { color: string; value: string }[]>;
    savingBenefitsImage?: string;
    savingBenefitsTitle?: string;
    subscriptionData?: {};
}

export interface ISuperSavingBottomWidgetProps {
    image: string;
    backgroundImage: string;
    lines: {
        labels: {
            type: string;
            value: string;
            opacity: number;
            color: string;
            bold: boolean;
            underlined: boolean;
            striked: boolean;
        }[];
        applicable: boolean;
        notApplicable: boolean;
        chevronFlag: boolean;
        title: boolean;
        bulletPoint: boolean;
    }[];
    subscriptionData: ISuperSavingBottomWidgetSubscriptionDataProps;
    savingBenefitsImage: string;
    girforder: boolean;
}

export interface ISuperSavingBottomWidgetSubscriptionDataProps {
    name: string;
    planId: string;
    cta: {
        text: string;
        link: string;
    };
    basePrice: string;
    finalPrice: string;
    displayCta: boolean;
    planType: string;
}

export interface IApplyCouponWidget {
    subtitle?: string;
    subtitleLogo?: string;
    title?: string;
    subtitleColour?: string;
    coupon?: string;
    value?: number;
    appliedCouponDiscount?: number;
}

export interface ICoupon {
    value?: number;
    coupon?: string;
    amount?: number;
}

interface IPartialPaymentOption {
    id: string;
    leftText: string;
    leftSubText: string;
    rightText: string;
    isDisabled: boolean;
    isSelected: boolean;
    disclaimers: string[];
}

export interface IHighlightedFooterCallout {
    text: string;
    image: string;
    backgroundColor: string;
    textColor: string;
}

/** Shown under partial-pay option when present on `partialPaymentWidget` from BE. */
export interface IDinecashEarningsNudge {
    logo: string;
    text: string;
}

export interface IPartialPaymentWidget {
    options: IPartialPaymentOption[];
    highlightedFooterCallout?: IHighlightedFooterCallout;
    dinecashEarningsNudge?: IDinecashEarningsNudge;
}

export interface ISelectedSlot {
    id: number;
    reservationTime: number;
    availableInventoryCount: number;
    slotGroupName: string;
}

export interface CartMetaData {
    billAmount?: number;
    couponCode: string | null;
    dealCategory?: string;
    requestTimestamp: number;
    restaurantId: string;
    restaurantTip?: number;
    rewardsWidget?: {
        rewardsRedeemed?: boolean;
        redeemableRewards?: number;
    };
    subscriptionPlanId?: string | null;
    referrer?: string;
    referrerId?: number;
    ticketCtx?: string;
    posCtx?: string; // This field has a JSON string that is used in the scan QR flow. which has details like reference_id, partner_id, is_scan_to_pay_transaction.
    type: string;
    tableId?: string;
    parentOrderId?: string | null;
    isPartialPaymentApplied?: boolean;
    adjustToAvailableQuantity?: boolean;
}

export interface IGroupedItems {
    brandId: string;
    itemTotalCount?: number;
    items: IResponseCartItem[];
}

export interface IBrandInfo {
    id: string;
    name?: string;
    logo?: string;
}
export interface IGeneralRedemptionSectionMeta {
    title: string;
    supertext: string;
    icon: string;
    sections: {
        key: string;
        title: string;
        icon: string;
        points: string[];
    }[];
}

export interface IRewardsPoolWidgetTooltip {
    text: string;
}

export enum RewardsPoolWidgetState {
    POOLED = "POOLED",
    INITIAL = "INITIAL",
}

export enum RewardsPoolWidgetType {
    WARN = "WARN",
    INFO = "INFO",
    ERROR = "ERROR",
}

export enum RewardsPoolFlowType {
    PAY_NOW = "PAY_NOW",
    RESERVATION = "RESERVATION",
}

export interface IRewardsPoolWidget {
    title: string;
    subtitle: string;
    logo: string;
    tag: string;
    state: RewardsPoolWidgetState;
    pooledAmount: string;
    totalPledgedAmount: number;
    maxNoOfContributor: number;
    noOfCurrentContributors: number;
    maxPoolAmountAllowed: number;
    maxPerHead: number;
    type: RewardsPoolWidgetType;
    toolTip: IRewardsPoolWidgetTooltip;
    flowType: RewardsPoolFlowType;
}

export interface IDineoutCartResponseData {
    applyCouponWidget?: IApplyCouponWidget;
    superSavingBottomWidget: ISuperSavingBottomWidgetProps;
    subscriptionWidget?: ISubscriptionWidget;
    superData?: {
        userStatus: string;
        subscriptionPlanId: string;
    };
    items: IResponseCartItem[];
    storesInfo: IStoresInfo[];
    bill: IDineoutCartResponseBillData;
    type?: string;
    aggregatedItemsInfo?: IAggregatedItemsInfo;
    widgets?: IWidget[] | undefined;
    showCouponWidget?: boolean;
    coupon?: ICoupon;
    callouts: ICallout[];
    cartId: string;
    footer: IFooter;
    bulkCart?: boolean;
    noRushDeliveryAvailable?: boolean;
    noRushEnabledForStore?: boolean;
    couponCard?: ICouponModalCard;
    rewardsWidget?: IRewardWidgetResponseData;
    partialPaymentWidget?: IPartialPaymentWidget;
    // it is applicable when brand-grouped cart data needs to be shown instead of items array. For example, steal store cart.
    groupedItems?: IGroupedItems[];
    brandInfo?: IBrandInfo[];
    generalRedemptionSectionMeta?: IGeneralRedemptionSectionMeta;
    rewardsPoolWidget?: IRewardsPoolWidget;
}

export interface ICouponModalCard {
    title: string;
    description: string;
    icon: string;
    cta: {
        text: string;
        color: string;
    };
}

export interface IBottomNudgeTextCallout {
    text: string;
    backgroundColor: keyof IColors;
    textColor: keyof IColors;
}

export interface IRewardWidgetResponseData {
    backgroundImage?: string;
    earnedRewards?: number;
    earnRewardsTitle?: string;
    disableEarnRewardsTitle?: boolean;
    logo?: string;
    redeemableRewards?: number;
    subtitle?: string;
    title?: string;
    rewardsRedeemed?: boolean;
    extraInfoText?: string;
    boosterBackgroundImage?: string;
    boosterAnimation?: string;
    isBooster?: boolean;
    header?: {
        title?: string;
        subtitle?: string;
        logo?: string;
        callout?: string;
    };
    isToggleDisabled?: boolean;
    bottomNudgeTextCallout?: IBottomNudgeTextCallout;
}

export interface IDineoutLocalCartItem {
    itemId?: string;
    quantity?: number;
    dealCategory?: string;
    selectedSlot?: ISelectedSlot;
    meta?: {
        variantId?: string;
    };
    serviceLine?: string;
    min?: number;
    max?: number;
    outOfStock?: boolean;
    itemPrice?: number;
    itemVariantId?: string;
}

export interface IDineoutLocalCart {
    flowType?: string;
    forceRefresh?: string;
    restaurantId?: string;
    dealCategory?: string;
    itemId?: string;
    count?: number;
    selectedSlot?: ISelectedSlot;
    cartMetaData?: CartMetaData;
    cartType?: string;
    items?: IDineoutLocalCartItem[];
    location?: {
        latitude: string;
        longitude: string;
    };
    source?: string;
    distanceFromRx?: string;
    rewardsWidget?: {
        rewardsRedeemed?: boolean;
        redeemableRewards?: number;
    };
    /** The source from where the pay bill is scanned or initiated (e.g., dineout_rdp_bottom, dineout_nba_widget etc.) */
    payBillSource?: string;
    /** The payment flow value indicating the type or method of payment processing (e.g., Scan QR) */
    paymentFlow?: string;
    latestCartCallTimestamp?: number;
    latestCartResponse?: IDineoutCartResponse;
}

export interface IDineoutCartResponse {
    statusCode: number;
    statusMessage?: string | null;
    statusTitle?: string;
    data: IDineoutCartResponseData | null;
    ctaText?: string;
    ctaAction?: string;
    ctaActionV2?: string; // TODO: this we need to deprecate and use ctaAction
    secondaryCtaText?: string;
    secondaryCtaAction?: string;
}

/** V2 local cart shape used only for steal deal cart flow. */
export interface IDineoutLocalCartResponseV2 {
    localCart: IDineoutLocalCart;
    latestCartCallTimestamp?: number;
    latestCartResponse?: IDineoutCartResponse;
    cartResponseThresholdInMs?: number;
}

export interface DineoutCartState {
    localCart: IDineoutLocalCart | null;
    reviewedCart: IDineoutCartResponse | null;
}
